import { Box, HStack, VStack, Text, Button, Divider, Grid } from '@chakra-ui/react';
import { getTeamColor } from '../theme/teamColors';

const getLastName = (fullName) => {
  if (!fullName) return "";
  const parts = fullName.split(' ');
  if (parts.length <= 1) return fullName;
  
  const lastPart = parts[parts.length - 1];
  const suffixes = ['Jr.', 'Sr.', 'II', 'III', 'IV', 'V', 'Jr', 'Sr'];
  
  if (suffixes.includes(lastPart) && parts.length >= 2) {
    return `${parts[parts.length - 2]} ${lastPart}`;
  }
  
  return lastPart;
};

/* Compact baseball diamond — rotated 45° square with base indicators */
const BaseDiamond = ({ first, second, third }) => {
  const baseSize = "12px";
  const activeBg = "brand.red";
  const emptyBg = "card.border";

  return (
    <Box w="40px" h="40px" position="relative">
      {/* 2nd base - top */}
      <Box
        w={baseSize} h={baseSize}
        bg={second ? activeBg : emptyBg}
        position="absolute" top="2px" left="50%"
        transform="translateX(-50%) rotate(45deg)"
        boxShadow={second ? "0 0 6px rgba(186,1,34,0.6)" : "none"}
      />
      {/* 3rd base - left */}
      <Box
        w={baseSize} h={baseSize}
        bg={third ? activeBg : emptyBg}
        position="absolute" top="50%" left="2px"
        transform="translateY(-50%) rotate(45deg)"
        boxShadow={third ? "0 0 6px rgba(186,1,34,0.6)" : "none"}
      />
      {/* 1st base - right */}
      <Box
        w={baseSize} h={baseSize}
        bg={first ? activeBg : emptyBg}
        position="absolute" top="50%" right="2px"
        transform="translateY(-50%) rotate(45deg)"
        boxShadow={first ? "0 0 6px rgba(186,1,34,0.6)" : "none"}
      />
    </Box>
  );
};

/* One count row: fixed-width label + filled/empty pips, so the B/S/O columns align */
const CountRow = ({ label, value, max, color }) => (
  <HStack spacing={1}>
    <Text fontSize="8px" fontWeight="bold" opacity={0.5} color={color} letterSpacing="wider" w="12px" lineHeight="1">
      {label}:
    </Text>
    {Array.from({ length: max }, (_, i) => (
      <Box key={i} w={2} h={2} bg={value >= i + 1 ? 'brand.red' : 'card.border'} />
    ))}
  </HStack>
);

/* Compact pitcher stat line: "Skenes (0-1, 67.50)" */
const PitcherLine = ({ label, name, wins, losses, era, saves, color = "white" }) => {
  if (!name) return null;
  const lastName = getLastName(name);
  let statLine = '';
  if (saves !== undefined) {
    statLine = `(${saves})`;
  } else if (wins !== undefined) {
    statLine = `(${wins}-${losses}, ${era})`;
  }
  return (
    <VStack spacing={0} align="center">
      <Text fontSize="7px" fontWeight="bold" opacity={0.5} color={color} letterSpacing="wider" textTransform="uppercase">
        {label}
      </Text>
      <Box textAlign="center">
        <Text fontSize={{ base: "10px", md: "xs" }} fontWeight="900" color={color} whiteSpace="nowrap" lineHeight="1.1">
          {lastName}
        </Text>
        {statLine && (
          <Text fontSize={{ base: "7px", md: "8px" }} fontWeight="bold" opacity={0.7} color={color} lineHeight="1">
            {statLine}
          </Text>
        )}
      </Box>
    </VStack>
  );
};

const LiveScoreboard = ({ gameData, liveData, lastPlay }) => {
  const home = gameData?.teams?.home?.abbreviation || "H";
  const away = gameData?.teams?.away?.abbreviation || "A";
  const homeScore = liveData?.linescore?.teams?.home?.runs ?? 0;
  const awayScore = liveData?.linescore?.teams?.away?.runs ?? 0;

  const homeColor = getTeamColor(home);
  const awayColor = getTeamColor(away);
  
  // Inning detail
  const inningHalf = liveData?.linescore?.inningHalf || ""; // "Top" or "Bottom"
  const currentInning = liveData?.linescore?.currentInning || "";
  const inningLabel = inningHalf && currentInning ? `${inningHalf} ${currentInning}` : "";

  // Current batter (last name only for compact display)
  const batterName = liveData?.linescore?.offense?.batter?.fullName || "";
  const batterLastName = getLastName(batterName);

  // Runner positions
  const onFirst = !!liveData?.linescore?.offense?.first;
  const onSecond = !!liveData?.linescore?.offense?.second;
  const onThird = !!liveData?.linescore?.offense?.third;

  // Game Status Logic
  const abstractState = gameData?.status?.abstractGameState;
  const detailedState = gameData?.status?.detailedState;
  
  const isFinal = abstractState === 'Final';
  const isLive = abstractState === 'Live';
  const isPreview = abstractState === 'Preview';

  // Real-time counts
  const balls = isFinal ? "-" : (liveData?.linescore?.balls ?? 0);
  const strikes = isFinal ? "-" : (liveData?.linescore?.strikes ?? 0);
  const outs = isFinal ? "-" : (liveData?.linescore?.outs ?? 0);

  // Team Records
  const homeRecord = gameData?.teams?.home?.record;
  const awayRecord = gameData?.teams?.away?.record;

  // Probable Pitchers (Pre-game)
  const probablePitchers = gameData?.probablePitchers;
  const boxscore = liveData?.boxscore?.teams;

  const getPitcherStats = (side, pitcherId) => {
    if (!pitcherId || !boxscore) return {};
    const player = boxscore[side]?.players?.[`ID${pitcherId}`];
    const stats = player?.seasonStats?.pitching || {};
    return { wins: stats.wins, losses: stats.losses, era: stats.era };
  };

  const awaySP = probablePitchers?.away;
  const homeSP = probablePitchers?.home;
  const awaySPStats = getPitcherStats('away', awaySP?.id);
  const homeSPStats = getPitcherStats('home', homeSP?.id);

  // Decisions (Final)
  const decisions = liveData?.decisions || {};
  const winner = decisions.winner;
  const loser = decisions.loser;
  const save = decisions.save;

  const getDecisionStats = (pitcherId) => {
    if (!pitcherId || !boxscore) return {};
    for (const side of ['away', 'home']) {
      const player = boxscore[side]?.players?.[`ID${pitcherId}`];
      if (player) {
        const stats = player?.seasonStats?.pitching || {};
        return { wins: stats.wins, losses: stats.losses, era: stats.era, saves: stats.saves };
      }
    }
    return {};
  };

  const winnerStats = getDecisionStats(winner?.id);
  const loserStats = getDecisionStats(loser?.id);
  const saveStats = getDecisionStats(save?.id);

  const getStatusProps = (abstract, detailed) => {
    switch (abstract) {
      case 'Live':
        if (detailed === 'Warmup') return { label: 'WARMUP', color: 'orange.500' };
        if (detailed === 'Delayed' || detailed === 'Suspended') return { label: detailed.toUpperCase(), color: 'yellow.600' };
        return { label: 'IN PROGRESS', color: 'brand.red' };
      case 'Final':
        return { label: 'FINAL SCORE', color: 'gray.500' };
      case 'Preview':
        const startTime = gameData?.datetime?.dateTime
          ? new Date(gameData.datetime.dateTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
          : 'SCHEDULED';
        return { label: startTime, color: 'brand.blue' };
      default:
        return { label: detailed?.toUpperCase() || 'UNKNOWN', color: 'gray.700' };
    }
  };

  const status = getStatusProps(abstractState, detailedState);
  const boardBg = "board.bg";
  const accentBg = "board.accent";
  const textColor = "hero.text";
  const borderColor = "card.border";

  // Show record below team name for Preview and Final, score for Live
  const showRecords = isPreview;

  return (
    <Box 
      bg={boardBg} 
      color="white" 
      borderRadius="none" 
      overflow="hidden"
      boxShadow="broadcast"
      border="1px solid"
      borderColor="card.border"
      w="full"
    >
      <Grid templateColumns="1fr 1px 1.4fr 1px 1fr" p={6} pb={4} alignContent="center">
        {/* Away Team */}
        <VStack spacing={1}>
          <Box 
            bg={awayColor} 
            color="white" 
            px={3} 
            py={1} 
            fontWeight="900" 
            fontSize="xl" 
            minW="70px" 
            h="40px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            lineHeight="1"
            borderRadius="sm"
          >
            {away}
          </Box>
          {showRecords && awayRecord ? (
            <Text fontSize="sm" fontWeight="900" color="hero.subtext">
              {awayRecord.wins}-{awayRecord.losses}
            </Text>
          ) : (
            <Text fontSize="4xl" fontWeight="900" color={textColor}>{awayScore}</Text>
          )}
        </VStack>
 
        <Divider orientation="vertical" borderColor={borderColor} height="auto" alignSelf="stretch" />

        {/* Game Status — Center Section */}
        <VStack spacing={2} justify="center" px={2}>
          {/* Inning: "Top 3" or "Bottom 7" — hidden when Final */}
          {!isFinal && (
            <Text fontSize="xs" fontWeight="900" letterSpacing="widest" textTransform="uppercase" color={textColor}>
              {inningLabel || detailedState || "---"}
            </Text>
          )}

          {/* Diamond + B/S/O count block — live games only */}
          {isLive && (
            <HStack spacing={4} align="center">
              <BaseDiamond first={onFirst} second={onSecond} third={onThird} />
              <VStack spacing="3px" align="flex-start">
                <CountRow label="B" value={Math.min(liveData?.linescore?.balls ?? 0, 3)} max={3} color={textColor} />
                <CountRow label="S" value={Math.min(liveData?.linescore?.strikes ?? 0, 2)} max={2} color={textColor} />
                <CountRow label="O" value={Math.min(liveData?.linescore?.outs ?? 0, 2)} max={2} color={textColor} />
              </VStack>
            </HStack>
          )}

          {/* Current batter — live games only */}
          {isLive && batterLastName && (
            <Text fontSize="9px" fontWeight="900" color="hero.subtext" letterSpacing="wider" textTransform="uppercase">
              AB: {batterLastName}
            </Text>
          )}

          <Button 
            bg={status.color} 
            color="white" 
            size="xs" 
            px={4} 
            fontSize="8px" 
            fontWeight="900" 
            borderRadius="none"
            _hover={{ opacity: 0.9 }}
            textTransform="uppercase"
          >
            {status.label}
          </Button>
        </VStack>

        <Divider orientation="vertical" borderColor={borderColor} height="auto" alignSelf="stretch" />

        {/* Home Team */}
        <VStack spacing={1}>
          <Box 
            bg={homeColor} 
            color="white" 
            px={3} 
            py={1} 
            fontWeight="900" 
            fontSize="xl" 
            minW="70px" 
            h="40px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            lineHeight="1"
            borderRadius="sm"
          >
            {home}
          </Box>
          {showRecords && homeRecord ? (
            <Text fontSize="sm" fontWeight="900" color="hero.subtext">
              {homeRecord.wins}-{homeRecord.losses}
            </Text>
          ) : (
            <Text fontSize="4xl" fontWeight="900" color={textColor}>{homeScore}</Text>
          )}
        </VStack>
      </Grid>
 
      {/* Bottom Bar — conditional based on game state */}
      <HStack bg={accentBg} py={2} px={{ base: 2, md: 6 }} justify="space-around" borderTop="1px solid" borderColor="whiteAlpha.300">
        {isPreview && (
          <Grid templateColumns="1fr 1px 1.4fr 1px 1fr" w="full" alignItems="center" justifyItems="center" gap={1}>
            <VStack spacing={0}>
              <Text fontSize={{ base: "10px", md: "xs" }} fontWeight="900" color="white" whiteSpace="nowrap" lineHeight="1.1">
                {getLastName(awaySP?.fullName) || "TBD"}
              </Text>
              {awaySPStats.wins !== undefined && (
                <Text fontSize={{ base: "7px", md: "9px" }} fontWeight="bold" opacity={0.6} color="white" lineHeight="1">
                  {awaySPStats.wins}-{awaySPStats.losses}, {awaySPStats.era}
                </Text>
              )}
            </VStack>
            <Box />
            <Box />
            <Box />
            <VStack spacing={0}>
              <Text fontSize={{ base: "10px", md: "xs" }} fontWeight="900" color="white" whiteSpace="nowrap" lineHeight="1.1">
                {getLastName(homeSP?.fullName) || "TBD"}
              </Text>
              {homeSPStats.wins !== undefined && (
                <Text fontSize={{ base: "7px", md: "9px" }} fontWeight="bold" opacity={0.6} color="white" lineHeight="1">
                  {homeSPStats.wins}-{homeSPStats.losses}, {homeSPStats.era}
                </Text>
              )}
            </VStack>
          </Grid>
        )}

        {isLive && (
          <>
            <VStack spacing={0}>
              <Text fontSize="8px" fontWeight="bold" opacity={0.6} color="white">BALLS</Text>
              <Text fontSize="xl" fontWeight="900" color="white">{balls}</Text>
            </VStack>
            <Divider orientation="vertical" height="20px" borderColor="whiteAlpha.400" />
            <VStack spacing={0}>
              <Text fontSize="8px" fontWeight="bold" opacity={0.6} color="white">STRIKES</Text>
              <Text fontSize="xl" fontWeight="900" color="white">{strikes}</Text>
            </VStack>
            <Divider orientation="vertical" height="20px" borderColor="whiteAlpha.400" />
            <VStack spacing={0}>
              <Text fontSize="8px" fontWeight="bold" opacity={0.6} color="white">OUTS</Text>
              <Text fontSize="xl" fontWeight="900" color="white">{outs}</Text>
            </VStack>
          </>
        )}

        {isFinal && (
          <Grid templateColumns={save ? "1fr 1fr 1fr" : "1fr 1fr"} w="full" justifyItems="center" gap={1}>
            <PitcherLine
              label="W"
              name={winner?.fullName}
              wins={winnerStats.wins}
              losses={winnerStats.losses}
              era={winnerStats.era}
            />
            <PitcherLine
              label="L"
              name={loser?.fullName}
              wins={loserStats.wins}
              losses={loserStats.losses}
              era={loserStats.era}
            />
            {save && (
              <PitcherLine
                label="S"
                name={save.fullName}
                saves={saveStats.saves}
              />
            )}
          </Grid>
        )}
      </HStack>

      {/* Last play ticker — single truncated line so layout height stays fixed */}
      {isLive && lastPlay && (
        <HStack
          bg={accentBg}
          py={1.5}
          px={3}
          spacing={2}
          borderTop="1px solid"
          borderColor="whiteAlpha.300"
          overflow="hidden"
        >
          <Text fontSize="7px" fontWeight="900" color="brand.red" letterSpacing="widest" flexShrink={0} bg="white" px={1.5} py={0.5} borderRadius="sm">
            LAST PLAY
          </Text>
          <Text fontSize="9px" fontWeight="bold" color="white" opacity={0.85} noOfLines={1} lineHeight="1.2">
            {lastPlay}
          </Text>
        </HStack>
      )}
    </Box>
  );
};

export default LiveScoreboard;
